// src/hooks/useAssetsRenewalsNotifications.js
//
// ⭐ NEW: Red-dot flag for the Assets & Renewals nav item. "Needs looking at"
// means either:
//   - a service renewal whose nextRenewalDate has passed (out of date), or
//   - an issued asset stuck as 'Lost' or 'Damaged' (never resolved back to
//     Returned/Issued), or
//   - ⭐ NEW: an asset (person, van or site) whose PAT test has expired and
//     hasn't been returned.
// Reuses the same aggregate endpoints the Assets & Renewals page itself
// calls (GET /staff/assets/all, GET /customers/service-renewals/all) rather
// than adding new backend routes just for a count. Live-updates via the
// 'assets-renewals-update' socket event emitted by every asset/renewal
// create/update/delete in staffController.js and customerController.js.
import { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../components/context/AuthContext';
import { useSocket } from '../components/context/SocketProvider';

export const useAssetsRenewalsNotifications = () => {
    const [overdueRenewalsCount, setOverdueRenewalsCount] = useState(0);
    const [assetsNeedingAttentionCount, setAssetsNeedingAttentionCount] = useState(0);
    const { user, loading } = useAuth();
    const socket = useSocket();

    const fetchCounts = async () => {
        // Only admins/managers see this page, so only they need the check
        if (!user || (user.role !== 'admin' && user.role !== 'manager')) return;

        try {
            const [renewalsRes, assetsRes] = await Promise.all([
                api.get('/customers/service-renewals/all'),
                api.get('/staff/assets/all'),
            ]);

            const today = new Date(new Date().toDateString());
            const overdue = (renewalsRes.data || []).filter(item => {
                return item.nextRenewalDate && new Date(item.nextRenewalDate) < today;
            }).length;
            setOverdueRenewalsCount(overdue);

            const needingAttention = (assetsRes.data || []).filter(asset => {
                if (asset.status === 'Lost' || asset.status === 'Damaged') return true;
                // ⭐ NEW: expired PAT test on an asset that's still out
                return asset.status !== 'Returned' && asset.patExpiryDate && new Date(asset.patExpiryDate) < today;
            }).length;
            setAssetsNeedingAttentionCount(needingAttention);
        } catch (err) {
            console.error('Error fetching assets & renewals counts', err);
        }
    };

    useEffect(() => {
        // ⭐ FIX: wait for the real session, not just the cached profile.
        // AuthContext hydrates `user` IMMEDIATELY from a cached localStorage
        // profile on page load, before the real __session cookie exists —
        // the login round trip is still in flight. Firing this fetch against
        // that cached-but-not-yet-authenticated state got a 403 every time
        // (harmless — it retries once the real session settles and `user`
        // updates again — but noisy). `loading` only goes false once auth
        // has genuinely resolved.
        if (loading) return;
        fetchCounts();

        // Live-updates the moment any asset or renewal is added/edited/deleted -
        // same pattern as useStockNotifications listening for stock-request events.
        if (socket) {
            socket.on('assets-renewals-update', fetchCounts);
            // Also refresh on reconnect, in case an update was missed while offline
            socket.on('connect', fetchCounts);
        }

        return () => {
            if (socket) {
                socket.off('assets-renewals-update', fetchCounts);
                socket.off('connect', fetchCounts);
            }
        };
    }, [user, loading, socket]);

    return {
        overdueRenewalsCount,
        assetsNeedingAttentionCount,
        needsAttention: overdueRenewalsCount > 0 || assetsNeedingAttentionCount > 0,
        refreshCounts: fetchCounts,
    };
};